import { AppState } from 'src/AppState/AppState';
import { RowMarketItemPropsType } from 'src/domains/sportsbook/trading/view/eventDetails/markets/eventMarketsItem/RowMarketItem';
import { action, computed, makeAutoObservable } from 'mobx';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { EventMarketItemType } from 'src_common/common/websocket2/modelsApi/EventMarkets';
import { getMarketStatus } from 'src/domains/sportsbook/trading/view/eventDetails/eventResults/EventResults.state';

export class RowMarketItemState {
    public constructor(
        private readonly appState: AppState,
        private readonly props: RowMarketItemPropsType
    ) {
        makeAutoObservable(this);
    }

    @action public onClickActiveHandler = async (): Promise<void> => {
        const requestArray = [];
        for (let i = 0; i < this.markets.length; i++) {
            const marketModel = this.markets[i];
            if (marketModel !== undefined) {
                const newState =
                    this.markets.length === 1 ? !marketModel.active : !this.props.eventMarketsItemState.isGroupActive;
                const callback = async (): Promise<void> => {
                    await this.appState.eventStateActions.saveMarketChanges(marketModel.id2, { active: newState });
                };
                requestArray.push(callback);
            }
        }

        for (const cb of requestArray) {
            await cb();
            await timeout(150);
        }
    };

    @action public onClickDisplayHandler = async (): Promise<void> => {
        const requestArray = [];
        for (let i = 0; i < this.markets.length; i++) {
            const market = this.markets[i];
            if (market !== undefined) {
                const newState =
                    this.markets.length === 1 ? !market.display : !this.props.eventMarketsItemState.isGroupDisplay;
                const callback = async (): Promise<void> => {
                    await this.appState.eventStateActions.saveMarketChanges(market.id2, { display: newState });
                };
                requestArray.push(callback);
            }
        }
        for (const cb of requestArray) {
            await cb();
            await timeout(150);
        }
    };

    @action public onClickLiabilities = (type: 'multiple' | 'single' | 'both'): void => {
        this.appState.routerBackoffice.closeEventPagePopup();
        //global variable was used because EventMarketsItem extends Form
        this.appState.reduxAction.onGetLiabilitiesRequest(this.props.marketId, type);
    };

    @action public onClickSingleLiabilities = (): void => {
        this.onClickLiabilities('single');
    };

    @action public onClickMultipleLiabilities = (): void => {
        this.onClickLiabilities('multiple');
    };

    @action public onAuditLinkHandler = (): void => {
        this.appState.reduxAction.onQueryAudit(`trading_content_market-change-${this.props.marketId.toOldId()}`, {
            topics: ['trading_content'],
            tags: [`market-change-${this.props.marketId.toOldId()}`],
        });
    };

    @computed private get getMarketState2(): string {
        const marketStatus = getMarketStatus(this.props.marketId);

        if (marketStatus === null) {
            return 'New';
        }

        const MARKET_STATES: Record<string, string> = {
            open: 'Open',
            new: 'New',
            resulted: 'Resulted',
            settled: 'Settled',
        };

        return MARKET_STATES[marketStatus] ?? 'New';
    }

    @computed public get getMarketState(): string {
        return this.getMarketState2;
    }

    @computed public get marketName(): string | null {
        const marketModel = this.props.marketId.getModel();

        if (marketModel === null) {
            return null;
        }

        const {
            id: marketId,
            name: marketName,
            template: { id: templateId, name },
        } = marketModel;

        if (this.markets.length <= 1) {
            return marketName;
        }

        const cachedTemplateName = this.props.eventMarketsItemState.getTemplateNameById(templateId);

        if (cachedTemplateName !== undefined) {
            return cachedTemplateName;
        }

        return name === '' ? marketId : name;
    }
    @action public onMarketNameClickHandler = (): void => {
        if (this.markets.length > 1) {
            this.props.eventMarketsItemState.toggleGroup();
        } else {
            this.props.toggle(this.props.shortname, this.props.marketId.toOldId());
        }
    };

    @computed private get marketsForInnerMarket(): Array<EventMarketItemType> {
        return this.props.eventMarketsItemState.groupMarkets
            .filter((innerMarket) => innerMarket.market.id2 === this.props.marketId)
            .map((inner) => inner.market);
    }

    @computed public get markets(): Array<EventMarketItemType> {
        return this.props.inner === true
            ? this.marketsForInnerMarket
            : this.props.eventMarketsItemState.groupMarkets.map((inner) => inner.market);
    }
}
